import { createSlice } from '@reduxjs/toolkit';
import { TAlertDialog } from 'shared/types/card.interface';
import { TModalIcons } from 'shared/types/modal.interface';
import { TPermissionValue, EPermissionValue } from 'shared/types/permission.interface';

type TModalData = {
  title: string;
  subtitle: string;
  icon?: TModalIcons | string;
  currentEmail?: string;
  type?: 0 | 1;
};

export interface ModalsState {
  alertDialog: {
    isOpen: boolean;
    data: TAlertDialog;
  };
  currentModal: {
    entityName: string;
    open: boolean;
    data: TModalData;
  };
  notifyAlert: {
    open: boolean;
    message: string;
    severity: TModalIcons;
  };
  shareModal: {
    isOpen: boolean;
    shareUrl: string;
    title?: string;
  };
  premissionModal: {
    isOpen: boolean;
    cameraStatus: TPermissionValue;
    microphoneStatus: TPermissionValue;
  };
}

const initialState: ModalsState = {
  alertDialog: {
    isOpen: false,
    data: null,
  },
  currentModal: {
    entityName: '',
    open: false,
    data: {
      title: '',
      subtitle: '',
    },
  },
  notifyAlert: { open: false, message: null, severity: null },
  shareModal: {
    isOpen: false,
    shareUrl: '',
    title: '',
  },
  premissionModal: {
    isOpen: false,
    cameraStatus: EPermissionValue.Granted,
    microphoneStatus: EPermissionValue.Granted,
  },
};

const modalsSlice = createSlice({
  name: 'modals',
  initialState,
  reducers: {
    openAlertDialog: (state, action) => {
      state.alertDialog.isOpen = true;
      state.alertDialog.data = action.payload;
    },
    closeAlertDialog: (state) => {
      state.alertDialog.isOpen = false;
      state.alertDialog.data = null;
    },
    openCurrentModal: (state, action) => {
      const { popupName, data } = action.payload;
      state.currentModal.entityName = popupName;
      if (data?.title) {
        state.currentModal.data.title = data.title;
      }
      if (data?.subtitle) {
        state.currentModal.data.subtitle = data.subtitle;
      }
      if (data?.icon) {
        state.currentModal.data.icon = data.icon;
      }
      if (data?.currentEmail) {
        state.currentModal.data.currentEmail = data.currentEmail;
      }
      if (data?.type !== undefined) {
        state.currentModal.data.type = data.type;
      }
      state.currentModal.open = true;
    },
    closeCurrentModal: (state) => {
      state.currentModal.entityName = '';
      state.currentModal.open = false;
      state.currentModal.data = {
        title: '',
        subtitle: '',
      };
    },
    setSnackbarNotify: (state, action) => {
      state.notifyAlert.open = action.payload.open;
      state.notifyAlert.message = action.payload.message;
      state.notifyAlert.severity = action.payload.severity;
    },
    openShareModal: (state, action) => {
      state.shareModal.isOpen = true;
      if (typeof action.payload === 'string') {
        state.shareModal.shareUrl = action.payload;
        state.shareModal.title = '';
      } else {
        state.shareModal.shareUrl = action.payload.url;
        state.shareModal.title = action.payload.title || '';
      }
    },
    closeShareModal: (state) => {
      state.shareModal.isOpen = false;
      state.shareModal.shareUrl = '';
      state.shareModal.title = '';
    },
    setCameraPermission: (state, action) => {
      if (!state.premissionModal.isOpen) state.premissionModal.isOpen = true;
      if (state.premissionModal.cameraStatus !== action.payload) {
        state.premissionModal.cameraStatus = action.payload;
      }
      if (
        action.payload === EPermissionValue.Granted &&
        state.premissionModal.microphoneStatus === EPermissionValue.Granted
      ) {
        state.premissionModal.isOpen = false;
      }
    },
    setMicrophonePermission: (state, action) => {
      if (!state.premissionModal.isOpen) state.premissionModal.isOpen = true;
      if (state.premissionModal.microphoneStatus !== action.payload) {
        state.premissionModal.microphoneStatus = action.payload;
      }
      if (
        state.premissionModal.cameraStatus === EPermissionValue.Granted &&
        action.payload === EPermissionValue.Granted
      ) {
        state.premissionModal.isOpen = false;
      }
    },
    closePermissionModal: (state) => {
      state.premissionModal.isOpen = false;
      state.premissionModal.cameraStatus = EPermissionValue.Granted;
      state.premissionModal.microphoneStatus = EPermissionValue.Granted;
    },
  },
  extraReducers: {},
});

export const {
  openAlertDialog,
  closeAlertDialog,
  openCurrentModal,
  closeCurrentModal,
  setSnackbarNotify,
  openShareModal,
  closeShareModal,
  setCameraPermission,
  setMicrophonePermission,
  closePermissionModal,
} = modalsSlice.actions;

export const modalsReducer = modalsSlice.reducer;
